import { z } from 'zod';

import { localeSchema, type Locale, type WorkspaceRole } from './enums.js';

/// Kimlik akışının FE/BE ortak sözleşmesi.
///
/// Doğrulama kuralları TEK yerde: form da bu şemayı çalıştırır, controller da.
/// İki tarafta ayrı yazılsaydı "frontend geçirdi, backend reddetti" farkı
/// kaçınılmazdı — kullanıcı formu doldurup gönderdikten sonra hata görürdü.

/// Parola alt sınırı 8. Karmaşıklık kuralı (büyük harf + rakam + sembol)
/// bilinçli olarak YOK: kullanıcıyı `Parola1!` gibi tahmin edilebilir
/// kalıplara itiyor. Uzunluk tek başına daha çok entropi veriyor.
const password = z
  .string()
  .min(8, 'Parola en az 8 karakter olmalı')
  .max(200, 'Parola en fazla 200 karakter olabilir');

const email = z
  .email('Geçerli bir e-posta adresi girin')
  .max(254) // RFC 5321
  .transform((v) => v.trim().toLowerCase());

// ─────────────────────────────── İstekler ───────────────────────────────

export const registerSchema = z.object({
  name: z.string().trim().min(2, 'Adınızı girin').max(120),
  email,
  password,
  /// Doğrulama e-postasının ve arayüzün dili.
  locale: localeSchema.default('tr'),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email,
  /// Girişte uzunluk kontrolü YAPILMAZ. Eski bir parola kuralın altındaysa
  /// "parola en az 8 karakter" demek, o hesabın var olduğunu ve parolasının
  /// kısa olduğunu sızdırır.
  password: z.string().min(1, 'Parolanızı girin'),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({ email });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({
  token: z.string().min(20, 'Bağlantı geçersiz'),
  password,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const verifyEmailSchema = z.object({
  token: z.string().min(20, 'Bağlantı geçersiz'),
});
export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

export const resendVerificationSchema = z.object({ email });
export type ResendVerificationInput = z.infer<typeof resendVerificationSchema>;

// ─────────────────────────────── Yanıtlar ───────────────────────────────

/// Oturumdaki kullanıcının arayüze dönen hâli.
/// `passwordHash` gibi alanların buraya sızmaması için yanıt tipi ayrı tutuluyor.
export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  locale: Locale;
  emailVerified: boolean;
  isPlatformAdmin: boolean;
}

/// Kullanıcının üyesi olduğu workspace'in özeti — switcher bunu listeler.
export interface SessionWorkspace {
  id: string;
  name: string;
  slug: string;
  role: WorkspaceRole;
}

export interface SessionResponse {
  user: SessionUser;
  workspaces: SessionWorkspace[];
  /// Etkin workspace kimliği; çerezden okunur, yoksa ilk üyelik.
  activeWorkspaceId: string | null;
}

export interface RegisterResponse {
  user: SessionUser;
  /// Doğrulama e-postası gerçekten gönderilebildi mi. `false` ise arayüz
  /// "tekrar gönder" düğmesini öne çıkarır — sessizce başarılı gibi
  /// davranmak kullanıcıyı boş yere gelen kutusunda bekletir.
  verificationEmailSent: boolean;
}

/// Hata gövdesi. Arayüz `code` üzerinden dallanır, `message` yalnızca
/// gösterim içindir — metin değişince davranış bozulmasın.
export const AuthErrorCode = {
  EMAIL_TAKEN: 'EMAIL_TAKEN',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  RATE_LIMITED: 'RATE_LIMITED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
} as const;
export type AuthErrorCode = (typeof AuthErrorCode)[keyof typeof AuthErrorCode];
